import { Permission } from '@idc-forms/permissions'
import { TabsContent } from '@radix-ui/react-tabs'
import { useMutation } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  Briefcase,
  CalendarDays,
  CalendarOff,
  CheckCircle,
  History,
  Info,
  Loader2,
  MessageSquare,
  Send,
  XCircle
} from 'lucide-react'
import { isValidElement, type ReactNode, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { ROUTE_ACTIONS } from '@/app/router/paths'
import { PATHS } from '@/app/router/routes'
import useAuth from '@/features/auth/hooks/use-auth'
import {
  useNegotiationsStore,
  useSubmitNegotiationStore
} from '@/features/negotiations/store/negotiations.store'
import { ProjectBadge } from '@/features/projects/components/project-badge'
import { QueryErrorState } from '@/shared/components/query-error-state/query-error-state'
import { dataProperties } from '@/shared/constants/dataProperties'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { cn } from '@/shared/lib/utils'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import { Avatar, AvatarFallback } from '@/shared/ui/avatar'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { handleMutationError } from '@/shared/utils/handle-api-error'
import { usePropertyDetail } from '../hooks/use-inventory-query'
import type { IInventoryDetail } from '../models/inventory'
import {
  INVENTORY_STATUS_COLORS,
  INVENTORY_STATUS_LABELS,
  InventoryStatus
} from '../models/inventory'
import { resendAuthorizationEmails } from '../services/inventory.services'
import { AuthorizeReservationDialog } from './authorize-reservation-dialog'
import InventoryComments from './comments/inventory-comments'
import { DeclineReservationDialog } from './decline-reservation-dialog'
import ReservationHistory from './history/reservation-history'

interface FieldRowProps {
  label: string
  value: any
}

function FieldRow({ label, value }: FieldRowProps) {
  const isEmptyValue = value === null || value === undefined || value === ''

  return (
    <div className="flex flex-col md:flex-row gap-2">
      <span className="block text-sm sm:w-[40%] text-muted-foreground">{label}</span>
      <div className="sm:w-[60%]">
        {isValidElement(value as ReactNode) ? value : isEmptyValue ? '-' : value}
      </div>
    </div>
  )
}

// function CalendarLinks ({ item }: { item: IInventoryItem }) {
//   if (!item.expiresAt) return null

//   const title = encodeURIComponent(`Apartado vence — ${item.code}`)
//   const details = encodeURIComponent(`El apartado del inmueble ${item.name} (${item.code}) vence en esta fecha.`)
//   const endDate = new Date(item.expiresAt)
//   const startStr = endDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
//   const endStr = startStr

//   const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&dates=${startStr}/${endStr}`
//   const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&body=${details}&startdt=${endDate.toISOString()}&enddt=${endDate.toISOString()}`

//   return (
//     <div className="flex flex-col gap-1">
//       <span className="text-[11px] text-muted-foreground">Agendar vencimiento en</span>
//       <div className="flex gap-2">
//         <a href={googleUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">Google</a>
//         <a href={outlookUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">Outlook</a>
//       </div>
//     </div>
//   )
// }

interface InventorySidePanelLoaderProps {
  propertyId: string
  onReserve: (item: IInventoryDetail) => void
  onRelease: (item: IInventoryDetail) => void
}

export function InventorySidePanelLoader({
  propertyId,
  onReserve,
  onRelease
}: InventorySidePanelLoaderProps) {
  const { data: detail, isLoading, error, refetch } = usePropertyDetail(propertyId)

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 p-3">
        <Skeleton className="h-6 w-3/4" />
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex gap-2">
              <Skeleton className="h-4 w-[40%]" />
              <Skeleton className="h-4 w-[50%]" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Sin carga en curso y sin detalle, la consulta falló: mostrarlo en vez de seguir fingiendo
  // que carga, que es lo que hacía cuando esta rama compartía el skeleton de arriba.
  if (!detail) {
    return (
      <div className="p-3">
        <QueryErrorState error={error} resource="el inmueble" onRetry={() => void refetch()} />
      </div>
    )
  }

  return (
    <InventorySidePanelContent
      item={detail}
      onReserve={() => {
        onReserve(detail)
      }}
      onRelease={() => {
        onRelease(detail)
      }}
    />
  )
}

interface InventorySidePanelContentProps {
  item: IInventoryDetail
  onReserve?: () => void
  onRelease?: () => void
}

function InventorySidePanelContent({ item, onReserve, onRelease }: InventorySidePanelContentProps) {
  const status = item.availabilityStatus
  const { has } = usePermissions()
  const { auth } = useAuth()
  const userId = auth?.[dataProperties.id]

  const canReserve = useMemo(() => {
    return has(Permission.CreateReservation) && status === InventoryStatus.AVAILABLE
  }, [has, status])

  const isReservedByMe =
    item.reservedBy?.[dataProperties.id] === userId &&
    (status === InventoryStatus.RESERVED || status === InventoryStatus.PENDING_AUTHORIZATION)
  const canRelease = isReservedByMe || has(Permission.ManageReservations)

  const canAuthorize =
    item.isCurrentUserAuthorizer && status === InventoryStatus.PENDING_AUTHORIZATION

  const canCreateNegotiation =
    has(Permission.CreateNegotiation) &&
    (status === InventoryStatus.AVAILABLE ||
      (isReservedByMe && status === InventoryStatus.RESERVED))
  const canViewHistory = has(Permission.ReadReservationHistory)
  const canViewComments = has(Permission.ReadInventoryComments)

  const navigate = useNavigate()
  const resetStoreNegotiation = useNegotiationsStore((s) => s.resetStoreNegotiation)
  const setNegotiation = useNegotiationsStore((s) => s.setNegotiation)
  const resetStoreSubmit = useSubmitNegotiationStore((s) => s.resetStoreSubmit)
  const setAction = useSubmitNegotiationStore((s) => s.setAction)

  const handleCreateNegotiation = () => {
    resetStoreNegotiation()
    resetStoreSubmit()
    setNegotiation({
      property: item._id,
      project: item.project?._id ?? '',
      society: item.project?.society?._id ?? ''
    })
    setAction(ROUTE_ACTIONS.create)
    navigate(PATHS.admin.negotiations.create)
  }

  const resendMutation = useMutation({
    mutationFn: async () => await resendAuthorizationEmails(item.reservationId!),
    onSuccess: () => toast.success('Correos de autorización reenviados'),
    onError: (error) => {
      handleMutationError(error)
    }
  })

  return (
    <div className="flex flex-col">
      <div className="pl-3 pt-3 pr-2">
        <h1 className="text-xl font-bold text-primary text-balance leading-snug">{item.name}</h1>
      </div>

      {/* Fields */}
      <div className="flex flex-col gap-2 py-3 pl-3 pr-2">
        <FieldRow label={'Sociedad'} value={item.project?.society?.name ?? '-'} />
        <FieldRow
          label={'Proyecto'}
          value={<ProjectBadge name={item.project?.name} code={item.project?.code} />}
        />
        <FieldRow
          label={'Tipo'}
          value={
            <div className="bg-accent text-accent-foreground px-1 py-0.5 w-fit rounded">
              <span className="text-sm font-medium">{item.type_property?.code}</span>
            </div>
          }
        />
        <FieldRow
          label={'Estado'}
          value={
            <span
              className={cn(
                'inline-flex items-center px-1.5 py-0.5 rounded-xs text-sm font-medium',
                INVENTORY_STATUS_COLORS[status]
              )}
            >
              {INVENTORY_STATUS_LABELS[status]}
            </span>
          }
        />
        {(status === InventoryStatus.RESERVED ||
          status === InventoryStatus.PENDING_AUTHORIZATION) && (
          <FieldRow
            label={'Apartado por'}
            value={
              <div className="flex items-center gap-1">
                <Avatar seed={item.reservedBy?.fullname} className="h-6 w-6">
                  <AvatarFallback className="text-xs">
                    {item.reservedBy?.fullname?.[0]}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm text-accent-foreground">{item.reservedBy?.fullname}</span>
              </div>
            }
          />
        )}

        {(status === InventoryStatus.SOLD || status === InventoryStatus.IN_NEGOTIATION) &&
          item.negotiationAgent && (
            <FieldRow
              label={status === InventoryStatus.SOLD ? 'Vendido por' : 'Asesor'}
              value={
                <div className="flex items-center gap-1">
                  <Avatar seed={item.negotiationAgent.fullname} className="h-6 w-6">
                    <AvatarFallback className="text-xs">
                      {item.negotiationAgent.fullname?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm text-accent-foreground">
                    {item.negotiationAgent.fullname}
                  </span>
                </div>
              }
            />
          )}

        {item.reservedDates && item.reservedDates.length > 0 && (
          <FieldRow
            label={'Fechas apartadas'}
            value={
              <div className="flex flex-col gap-1">
                {item.reservedDates.map((date) => (
                  <span key={date} className="text-sm text-accent-foreground font-medium">
                    {format(parseISO(date), "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })}
                  </span>
                ))}
              </div>
            }
          />
        )}

        {canAuthorize && <FieldRow label={'Nota'} value={item.notes} />}

        {canReserve && (
          <div className="w-full flex flex-col gap-1" onClick={onReserve}>
            <Button className="w-full">
              <CalendarDays className="size-5" /> Apartar Inmueble
            </Button>
            {item.requiresAuthorization && status === InventoryStatus.AVAILABLE && (
              <span className="text-xs text-muted-foreground text-center inline-block w-full">
                Autorización requerida
              </span>
            )}
          </div>
        )}

        {canRelease &&
          (status === InventoryStatus.RESERVED ||
            status === InventoryStatus.PENDING_AUTHORIZATION) && (
            <div className="w-full flex flex-col gap-1">
              <div className=" flex items-center gap-1.5">
                <Button variant="outline" className="flex-1" onClick={onRelease}>
                  <CalendarOff className="size-5" />
                  {isReservedByMe && status === InventoryStatus.PENDING_AUTHORIZATION
                    ? 'Cancelar solicitud'
                    : 'Liberar Inmueble'}
                </Button>
                {isReservedByMe && status === InventoryStatus.PENDING_AUTHORIZATION && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="secondary"
                        size={'icon'}
                        onClick={() => {
                          resendMutation.mutate()
                        }}
                        disabled={resendMutation.isPending}
                      >
                        {resendMutation.isPending ? (
                          <Loader2 className="size-5 animate-spin" />
                        ) : (
                          <Send className="size-5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Reenviar correo de apartado</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
          )}

        {canAuthorize && (
          <div className="w-full flex flex-col gap-1.5">
            <AuthorizeReservationDialog
              reservation={{ id: item.reservationId as string, publicId: item.reservationPublicId }}
              propertyName={item.name}
              reservedByName={item.reservedBy?.fullname}
            >
              <Button className="w-full">
                <CheckCircle className="size-5" /> Aceptar Solicitud
              </Button>
            </AuthorizeReservationDialog>
            <DeclineReservationDialog
              reservation={{
                id: item.reservationId as string,
                publicId: item.reservationPublicId
              }}
              propertyName={item.name}
            >
              <Button className="w-full" variant="outline">
                <XCircle className="size-5" /> Rechazar Solicitud
              </Button>
            </DeclineReservationDialog>
          </div>
        )}

        {canCreateNegotiation && (
          <div className="w-full">
            <Button variant="outline" className="w-full" onClick={handleCreateNegotiation}>
              <Briefcase className="size-5" /> Nueva negociación
            </Button>
          </div>
        )}

        {/*
        <CalendarLinks item={item} />
        */}
      </div>

      <div>
        <Tabs defaultValue="more-info">
          <TabsList className="w-full justify-normal rounded-none">
            <TabsTrigger className="flex gap-1" value="more-info">
              <Info className="size-4" /> Más información
            </TabsTrigger>
            {canViewHistory && (
              <TabsTrigger className="flex gap-1" value="history">
                <History className="size-4" /> Historial
              </TabsTrigger>
            )}
            {canViewComments && (
              <TabsTrigger className="flex gap-1" value="comments">
                <MessageSquare className="size-4" /> Comentarios
              </TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="more-info">
            <Accordion type="multiple">
              <AccordionItem value="construction-data">
                <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border ">
                  Datos de construcción
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                    <FieldRow label="Construcción M2" value={item.constructionM2 ?? null} />
                    <FieldRow label="Terreno M2" value={item.terrainM2 ?? null} />
                    <FieldRow label="Terreno V2" value={item.terrainV2 ?? null} />
                    <FieldRow label="Terraza M2" value={item.terraceM2 ?? null} />
                    <FieldRow label="Parqueo M2" value={item.parkingM2 ?? null} />
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="additional-data">
                <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                  Datos adicionales
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                    <FieldRow label="Finca" value={item.property ?? null} />
                    <FieldRow label="Folio" value={item.folio ?? null} />
                    <FieldRow label="Libro" value={item.book ?? null} />
                    <FieldRow label="Monto IUSI" value={item.iusiAmount ?? null} />
                    <FieldRow label="Contador Agua" value={item.waterMeter ?? null} />
                    <FieldRow label="Contador Electricidad" value={item.electricityMeter ?? null} />
                    <FieldRow label="Teléfono" value={item.telephone ?? null} />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>
          {canViewHistory && (
            <TabsContent value="history">
              <ReservationHistory propertyId={item[dataProperties.id]} />
            </TabsContent>
          )}
          {canViewComments && (
            <TabsContent value="comments">
              <InventoryComments
                propertyId={item[dataProperties.id]}
                propertyPublicId={item.publicId}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  )
}
